import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import grapesjs, { Editor as GrapesEditor } from 'grapesjs';
import newsletter from 'grapesjs-preset-newsletter';
// @ts-ignore: CSS side-effect import without type declarations
import 'grapesjs/dist/css/grapes.min.css';
import { toast, ToastContainer } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

export interface Template {
  id: string;
  name: string;
  description?: string;
  version?: number;
  html: string;
  css: string;
  createdAt: number | string;
  thumbnail?: string | null;
  isPrebuilt?: boolean;
  isUserDefined?: boolean;
  status?: string;
  category?: string;
  blocks?: any[];
}

interface EditorProps {
  template?: Template | null;
  onSave: (payload: {
    html: string;
    css: string;
    blocks: any[];
    styles: string;
    thumbnail?: string | null;
  }) => void;
  onGenerateThumbnail?: (payload: {
    html: string;
    css: string;
  }) => Promise<string | null>;
  onContentChange?: (hasChanges: boolean) => void;
  hasChanges?: boolean;
  isUpdate?: boolean;
  isSavingCopy?: boolean;
  isPrebuilt?: boolean;
}

export interface EditorHandle {
  handleSave: () => void;
  handlePreview: () => void;
  checkContentChanges: () => boolean;
}

// ⭐ Merge Tag interface
interface MergeTag {
  tag: string;
  description: string;
}

interface MergeTagsResponse {
  system: MergeTag[];
  organization: MergeTag[];
}

const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      template,
      onSave,
      onGenerateThumbnail,
      onContentChange,
      hasChanges = false,
      isUpdate = false,
      isSavingCopy = false,
      isPrebuilt = false,
    },
    ref
  ) => {
    const editorRef = useRef<GrapesEditor | null>(null);
    const containerId = 'gjs';
    const initialContentRef = useRef<{ html: string; css: string } | null>(null);
    const isInitialLoadRef = useRef(true);

    // ⭐ State for merge tags
    const [mergeTags, setMergeTags] = useState<MergeTagsResponse | null>(null);
    // const [loadingMergeTags, setLoadingMergeTags] = useState(false);
    const mergePanelRef = useRef<HTMLDivElement | null>(null);

    // ⭐ Fetch merge tags from API
    const fetchMergeTags = useCallback(async () => {
      try {
        // setLoadingMergeTags(true);
        const { token, tenantId, userId } = getAuthContext();

        if (!token || !tenantId) {
          console.warn('⚠️ Authentication required for merge tags');
          // setLoadingMergeTags(false);
          return;
        }


        const response = await fetch(
          `${BASE_URL}/v1/templates/merge-tags`,
          {
            method: 'GET',
            headers: {
              'X-Tenant-Id': tenantId,
              'X-User-Id': userId || '',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setMergeTags(data);
      } catch (error: any) {
        // console.error('❌ Failed to fetch merge tags:', error);
        toast.error(error.message || 'Failed to load merge tags');
      }
      // finally {
      //   setLoadingMergeTags(false);
      // }
    }, []);

    // ⭐ Fetch merge tags on mount
    useEffect(() => {
      fetchMergeTags();
    }, [fetchMergeTags]);

    const checkContentChanges = useCallback(() => {
      if (!editorRef.current || !initialContentRef.current) {
        return false;
      }

      const currentHtml = editorRef.current.getHtml();
      const currentCss = editorRef.current.getCss();

      const hasHtmlChanged = currentHtml !== initialContentRef.current.html;
      const hasCssChanged = currentCss !== initialContentRef.current.css;

      return hasHtmlChanged || hasCssChanged;
    }, []);

    const notifyContentChange = useCallback(() => {
      if (onContentChange) {
        const hasChanges = checkContentChanges();
        onContentChange(hasChanges);
      }
    }, [onContentChange, checkContentChanges]);

    // ⭐ Build merge tags options HTML
    const buildMergeTagsHTML = useCallback(() => {
      if (!mergeTags) {
        return `
          <div style="
            padding: 12px 14px;
            border-top: 1px solid #e2e8f0;
            background: #ffffff;
          ">
            <div style="
              display: inline-block;
              color: black;
              font-size: 11px;
              font-weight: 700;
              margin-bottom: 8px;
              letter-spacing: 0.3px;
            ">
              MERGE TAG
            </div>
            <div style="
              padding: 8px 12px;
              color: #94a3b8;
              font-size: 13px;
              text-align: center;
            ">
              Loading merge tags...
            </div>
          </div>
        `;
      }

      let optionsHtml = '<option value="">Select</option>';

      // System tags (flat, no optgroup header)
      if (mergeTags.system && mergeTags.system.length > 0) {
        mergeTags.system.forEach((tag) => {
          optionsHtml += `<option value="{{${tag.tag}}}">${tag.tag} - ${tag.description}</option>`;
        });
      }

      // Organization tags (flat, no optgroup header)
      if (mergeTags.organization && mergeTags.organization.length > 0) {
        mergeTags.organization.forEach((tag) => {
          optionsHtml += `<option value="{{${tag.tag}}}">${tag.tag} - ${tag.description}</option>`;
        });
      }

      return `
        <div style="
          padding: 12px 14px;
          border-top: 1px solid #e2e8f0;
          background: #ffffff;
        ">
          <div style="
            display: inline-block;
            color: black;
            font-size: 11px;
            font-weight: 700;
            margin-bottom: 8px;
            letter-spacing: 0.3px;
          ">
            MERGE TAG
          </div>
          <select id="merge-tag-select" style="
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            background: #f8fafc;
            font-size: 13px;
            color: #334155;
            outline: none;
            cursor: pointer;
          ">
            ${optionsHtml}
          </select>
        </div>
      `;
    }, [mergeTags]);

    // ⭐ Update merge panel when merge tags change
    useEffect(() => {
      if (!mergePanelRef.current) return;

      const panel = mergePanelRef.current;
      panel.innerHTML = buildMergeTagsHTML();

      // Handle tag insertion
      const selectEl = panel.querySelector(
        '#merge-tag-select'
      ) as HTMLSelectElement;

      selectEl?.addEventListener('change', (e) => {
        const value = (e.target as HTMLSelectElement).value;
        if (!value || !editorRef.current) return;

        const selected = editorRef.current.getSelected();
        if (selected && selected.is('text')) {
          selected.append(value);
        } else {
          editorRef.current.addComponents(`<div>${value}</div>`);
        }

        selectEl.value = '';
        toast.success(`Inserted ${value}`);
      });
    }, [mergeTags, buildMergeTagsHTML]);

    useEffect(() => {
      if (!editorRef.current) {
        editorRef.current = grapesjs.init({
          container: `#${containerId}`,
          height: '600px',
          storageManager: { autoload: false },
          components: template?.html || '',
          style: template?.css || '',

          assetManager: {
            upload: false,
            uploadFile: async (e: any) => {
              return new Promise<void>((resolve) => {
                const files = e.dataTransfer
                  ? e.dataTransfer.files
                  : e.target.files;
                const maxSize = 1 * 1024 * 1024;

                if (!files || !files.length) {
                  resolve();
                  return;
                }

                const file = files[0];

                if (file.size > maxSize) {
                  toast.error('Image size cannot exceed 1MB', {
                    position: 'top-right',
                    autoClose: 3000,
                  });
                  resolve();
                  return;
                }

                const reader = new FileReader();

                reader.onload = () => {
                  if (editorRef.current) {
                    editorRef.current.AssetManager.add({
                      src: reader.result as string,
                    });
                  }
                  resolve();
                };

                reader.readAsDataURL(file);
              });
            },
          },

          plugins: [newsletter],
          pluginsOpts: { newsletter: {} },
        });

        // Listen to content changes
        editorRef.current.on('component:update', () => {
          if (!isInitialLoadRef.current) {
            notifyContentChange();
          }
        });

        editorRef.current.on('style:update', () => {
          if (!isInitialLoadRef.current) {
            notifyContentChange();
          }
        });

        editorRef.current.on('change:selectedComponent', () => {
          if (!isInitialLoadRef.current) {
            notifyContentChange();
          }
        });

        // Custom save button
        const pn = editorRef.current.Panels;
        const commands = editorRef.current.Commands;

        pn.addButton('options', {
          id: 'save-template',
          className: 'fa fa-save',
          command: () => {
            const shouldDisable = isPrebuilt
              ? !hasChanges
              : isUpdate && !isSavingCopy && !hasChanges;

            if (shouldDisable) {
              const message = isPrebuilt
                ? 'No changes detected. Make some changes to save a copy of this template.'
                : 'No changes detected. Make some changes to save the template.';
              toast.info(message, { autoClose: 3000 });
              return;
            }
            commands.run('core:component-outline');
          },
          attributes: {
            title:
              isPrebuilt && !hasChanges
                ? 'No changes to save as copy'
                : isUpdate && !isSavingCopy && !hasChanges
                  ? 'No changes to save'
                  : 'Save Template',
          },
        });

        // ============================================
        // MERGE TAGS PANEL - Create container
        // ============================================
        const mergePanel = document.createElement('div');
        mergePanel.id = 'merge-tags-panel';
        mergePanelRef.current = mergePanel;

        // Append to the main editor container
        const editorEl = editorRef.current.getContainer();
        if (editorEl) {
          editorEl.style.position = 'relative';
          editorEl.appendChild(mergePanel);
        }

        // Initial render of merge tags
        if (mergePanelRef.current) {
          mergePanelRef.current.innerHTML = buildMergeTagsHTML();

          // Handle tag insertion
          const selectEl = mergePanelRef.current.querySelector(
            '#merge-tag-select'
          ) as HTMLSelectElement;

          selectEl?.addEventListener('change', (e) => {
            const value = (e.target as HTMLSelectElement).value;
            if (!value || !editorRef.current) return;

            const selected = editorRef.current.getSelected();
            if (selected && selected.is('text')) {
              selected.append(value);
            } else {
              editorRef.current.addComponents(`<div>${value}</div>`);
            }

            selectEl.value = '';
            toast.success(`Inserted ${value}`);
          });
        }
      }

      return () => {
        if (editorRef.current) {
          try {
            editorRef.current.destroy();
            editorRef.current = null;
          } catch (e) {
            // ignore
          }
        }
      };
    }, [buildMergeTagsHTML, notifyContentChange]);

    // Update save button state
    useEffect(() => {
      if (editorRef.current) {
        const pn = editorRef.current.Panels;
        const saveButton = pn.getButton('options', 'save-template');
        if (saveButton) {
          const shouldDisable = isPrebuilt
            ? !hasChanges
            : isUpdate && !isSavingCopy && !hasChanges;

          if (shouldDisable) {
            saveButton.set('active', false);
            const el = saveButton.get('el');
            if (el) {
              el.classList.add('gjs-pn-btn-disabled');
              el.style.opacity = '0.5';
              el.style.cursor = 'not-allowed';
            }
          } else {
            const el = saveButton.get('el');
            if (el) {
              el.classList.remove('gjs-pn-btn-disabled');
              el.style.opacity = '1';
              el.style.cursor = 'pointer';
            }
          }
        }
      }
    }, [hasChanges, isUpdate, isSavingCopy, isPrebuilt]);

    // Load template content
    useEffect(() => {
      if (!editorRef.current) return;

      isInitialLoadRef.current = true;

      if (template) {
        if (template.html) {
          editorRef.current.setComponents(template.html);
        } else if (template.blocks) {
          const combinedHtml = template.blocks
            .filter((block: any) => block.type === 'rawHtml')
            .map((block: any) => block.text)
            .join('\n');

          if (combinedHtml) {
            editorRef.current.setComponents(combinedHtml);
          }
        }
        editorRef.current.setStyle(template.css || '');
      } else {
        editorRef.current.setComponents(
          '<section class="section"><div class="container"><h1 style="text-align:center">New Template</h1><p style="text-align:center">Start editing...</p></div></section>'
        );
        editorRef.current.setStyle(`
          html, body {
            height: 100%;
            margin: 0;
            padding: 0;
          }
          .section {
            min-height: 100vh;
            box-sizing: border-box;
          }
          .container {
            min-height: 100vh;
            box-sizing: border-box;
          }
        `);
      }

      if (editorRef.current) {
        initialContentRef.current = {
          html: editorRef.current.getHtml(),
          css: editorRef.current.getCss() || '',
        };

        setTimeout(() => {
          isInitialLoadRef.current = false;
          if (onContentChange) {
            onContentChange(false);
          }
        }, 100);
      }
    }, [template]);

    const handleSave = async () => {
      if (!editorRef.current) return;

      const editor = editorRef.current;
      const fullHtml = editor.getHtml();
      const css = editor.getCss() || '';

      let bodyContent = fullHtml;

      const bodyRegex = /<body[^>]*>([\s\S]*)<\/body>/i;
      const bodyMatch = bodyContent.match(bodyRegex);

      if (bodyMatch && bodyMatch[1]) {
        bodyContent = bodyMatch[1].trim();
      }

      const blocks = [
        {
          type: 'rawHtml',
          text: bodyContent,
        },
      ];

      const components = editor.getComponents();
      const extraTextBlocks: string[] = [];

      const findTextBlocks = (comp: any) => {
        const html = comp.toHTML();
        const type = comp.get('type');

        if (type === 'text' || type === 'paragraph') {
          const plainText = html.replace(/<[^>]+>/g, '').trim();
          if (plainText && !bodyContent.includes(plainText)) {
            extraTextBlocks.push(plainText);
          }
        }

        const children = comp.get('components') || [];
        children.forEach(findTextBlocks);
      };

      components.forEach(findTextBlocks);

      if (extraTextBlocks.length > 0) {
        extraTextBlocks.forEach((text: string) => {
          if (text) {
            blocks.push({ type: 'paragraph', text });
          }
        });
      }

      let thumbnail: string | null = null;
      if (onGenerateThumbnail) {
        try {
          thumbnail = await onGenerateThumbnail({ html: fullHtml, css });
        } catch (e) {
          thumbnail = null;
        }
      }

      onSave({
        html: fullHtml,
        css,
        blocks,
        styles: css,
        thumbnail,
      });
    };

    const handlePreview = () => {
      if (!editorRef.current) return;
      const html = editorRef.current.getHtml();
      const css = editorRef.current.getCss();

      const templateName = template?.name || 'Untitled Template';
      const templateCategory = template?.category || 'Uncategorized';
      const templateVersion = template?.version || 1;
      const templateDescription =
        template?.description ||
        'A custom email template created from the editor.';
      const templateStatus = template?.status || 'DRAFT';
      const templateId = template?.id || 'new-template';
      const templateCreatedAt = template?.createdAt
        ? new Date(template.createdAt).toLocaleDateString()
        : new Date().toLocaleDateString();

      const win = window.open('', '_blank');
      if (win) {
        win.document.write(`
          <!DOCTYPE html>
          <html lang="en">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Preview: ${templateName}</title>
            <style>
              ${css}
              
              body {
                margin: 0;
                padding: 0;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              }
              
              .preview-container {
                max-width: 1200px;
                margin: 0 auto;
                padding: 20px;
              }
              
              .preview-card {
                background: white;
                border-radius: 16px;
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                overflow: hidden;
                margin-top: 40px;
                margin-bottom: 40px;
              }
              
              .preview-header {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 24px 32px;
                position: relative;
              }
              
              .preview-title {
                margin: 0;
                font-size: 28px;
                font-weight: 700;
                letter-spacing: -0.5px;
              }
              
              .preview-meta {
                display: flex;
                gap: 12px;
                margin-top: 16px;
                flex-wrap: wrap;
              }
              
              .preview-badge {
                background: rgba(255, 255, 255, 0.2);
                color: white;
                padding: 6px 16px;
                border-radius: 20px;
                font-size: 14px;
                font-weight: 500;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.3);
              }
              
              .preview-description {
                background: rgba(255, 255, 255, 0.1);
                padding: 16px;
                border-radius: 12px;
                margin-top: 16px;
                font-size: 15px;
                line-height: 1.5;
                border: 1px solid rgba(255, 255, 255, 0.2);
              }
              
              .template-content {
                padding: 40px;
                background: white;
                min-height: 400px;
              }
              
              .preview-footer {
                background: #f8fafc;
                padding: 20px 32px;
                border-top: 1px solid #e2e8f0;
                display: flex;
                justify-content: space-between;
                align-items: center;
                color: #64748b;
                font-size: 14px;
              }
              
              .footer-info {
                display: flex;
                gap: 24px;
              }
              
              .preview-mode-indicator {
                background: #ecfdf5;
                border: 1px solid #a7f3d0;
                color: #065f46;
                padding: 12px 20px;
                border-radius: 8px;
                margin: 20px 32px 0;
                font-size: 14px;
                display: flex;
                align-items: center;
                gap: 8px;
              }
              
              .preview-mode-indicator::before {
                content: "👁️";
                font-size: 16px;
              }
              
              @media (max-width: 768px) {
                .preview-container {
                  padding: 10px;
                }
                
                .preview-header {
                  padding: 20px;
                }
                
                .preview-title {
                  font-size: 24px;
                }
                
                .template-content {
                  padding: 20px;
                }
                
                .preview-footer {
                  flex-direction: column;
                  gap: 12px;
                  align-items: flex-start;
                }
              }
            </style>
          </head>
          <body>
            <div class="preview-container">
              <div class="preview-card">
                <div class="preview-header">
                  <h1 class="preview-title">${templateName}</h1>
                  <div class="preview-meta">
                    <span class="preview-badge">${templateStatus === 'UNDER_REVIEW'
            ? '🚀 Under Review'
            : '📝 ' +
            (templateStatus === 'DRAFT'
              ? 'Draft'
              : templateStatus)
          }</span>
                    <span class="preview-badge">📁 ${templateCategory}</span>
                    <span class="preview-badge">🔢 Version ${templateVersion}</span>
                    <span class="preview-badge">📅 ${templateCreatedAt}</span>
                  </div>
                  ${templateDescription
            ? `
                    <div class="preview-description">
                      ${templateDescription}
                    </div>
                  `
            : ''
          }
                </div>
                
                <div class="preview-mode-indicator">
                  This is a live preview of your template. Changes made in the editor will be reflected here.
                </div>
                
                <div class="template-content">
                  ${html ||
          '<div style="text-align: center; padding: 60px 20px; color: #94a3b8;"><p>No template content available</p><p style="font-size: 12px; margin-top: 8px;">Start adding content in the editor</p></div>'
          }
                </div>
                
                <div class="preview-footer">
                  <div class="footer-info">
                    <span>Template ID: ${templateId}</span>
                    <span>Status: ${templateStatus}</span>
                    <span>Preview generated on ${new Date().toLocaleDateString()}</span>
                  </div>
                  <span>Close this window to return to editor</span>
                </div>
              </div>
              
              <div style="text-align: center; color: white; padding: 20px; font-size: 14px; opacity: 0.8;">
                <p>This is a live preview. Close this window to continue editing.</p>
              </div>
            </div>
            
            <script>
              document.addEventListener('DOMContentLoaded', function() {
                window.focus();
                
                document.addEventListener('keydown', function(e) {
                  if (e.key === 'Escape') {
                    window.close();
                  }
                });
                
              });
            </script>
          </body>
          </html>
        `);
        win.document.close();
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
      handlePreview,
      checkContentChanges,
    }));

    return (
      <div className="flex flex-col h-full relative">
        <div id={containerId} className="bg-white rounded-b shadow p-2" />

        {/* Disabled save button styles */}
        <style>{`
          .gjs-pn-btn-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
            pointer-events: none !important;
          }
          
          .gjs-pn-btn-disabled:hover {
            background-color: #f1f5f9 !important;
          }
          
          .gjs-pn-btn-disabled::after {
            content: "${isPrebuilt ? 'No changes to save as copy' : 'No changes to save'
          }";
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: #1e293b;
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            white-space: nowrap;
            display: none;
            z-index: 1000;
          }
          
          .gjs-pn-btn-disabled:hover::after {
            display: block;
          }
        `}</style>

        {/* Light Theme + Merge Tags Positioning (no cut-off) */}
        <style>{`
          /* Root variables */
          #${containerId} {
            --gjs-primary-color: #3498db;
            --gjs-bg-light: #f8fafc;
            --gjs-bg-panel: #ffffff;
            --gjs-text-color: #1e293b;
            --gjs-border-color: #e2e8f0;
          }

          /* All panels */
          #${containerId} .gjs-pn-panel,
          #${containerId} .gjs-pn-views,
          #${containerId} .gjs-pn-views-container,
          #${containerId} .gjs-pn-commands,
          #${containerId} .gjs-pn-buttons {
            background-color: #ffffff !important;
            color: #1e293b !important;
            border-color: #e2e8f0 !important;
          }

          /* ===== MERGE TAGS PANEL ===== */
          #merge-tags-panel {
            position: absolute !important;
            right: 18px !important;
            bottom: 0 !important;
            width: 200px !important;
            z-index: 100 !important;
            background: white !important;
          }

          /* Reserve space so sidebar content is NOT cut off */
          #${containerId} .gjs-pn-views-container {
            padding-bottom: 95px !important;
            overflow-y: auto !important;
          }

          /* Panel titles */
          #${containerId} .gjs-pn-panel .gjs-title,
          #${containerId} .gjs-sm-sector-title,
          #${containerId} .gjs-layers-title,
          #${containerId} .gjs-trt-trait-title,
          #${containerId} .gjs-mdl-title {
            background-color: #f1f5f9 !important;
            color: #1e293b !important;
            border-bottom: 1px solid #e2e8f0 !important;
          }
            
          #${containerId} .gjs-rte-toolbar-ui {
            background-color: #f1f5f9 !important;
          }

          /* Buttons */
          #${containerId} .gjs-pn-btn {
            background-color: #f1f5f9 !important;
            color: #1e293b !important;
            border: 1px solid #cbd5e1 !important;
          }

          #${containerId} .gjs-pn-btn:hover {
            background-color: #e2e8f0 !important;
          }

          #${containerId} .gjs-pn-btn.gjs-pn-active {
            background-color: #3498db !important;
            color: white !important;
          }

          /* Icons */
          #${containerId} .gjs-pn-btn svg,
          #${containerId} svg {
            fill: #4a5568 !important;
          }

          #${containerId} .gjs-pn-btn.gjs-pn-active svg {
            fill: white !important;
          }

          /* Blocks panel */
          #${containerId} .gjs-blocks-c,
          #${containerId} .gjs-block {
            background-color: #ffffff !important;
          }

          #${containerId} .gjs-block {
            border: 1px solid #e2e8f0 !important;
            background-color: #f8fafc !important;
            color: #1e293b !important;
          }

          #${containerId} .gjs-block:hover {
            background-color: #e0f2fe !important;
            border-color: #7dd3fc !important;
          }

          #${containerId} .gjs-block-label {
            color: #1e293b !important;
          }

          /* Style Manager */
          #${containerId} .gjs-sm-sector,
          #${containerId} .gjs-sm-properties,
          #${containerId} .gjs-sm-property,
          #${containerId} .gjs-sm-field,
          #${containerId} .gjs-field {
            background-color: #f8fafc !important;
            color: #1e293b !important;
            border-color: #cbd5e1 !important;
          }

          #${containerId} .gjs-sm-label,
          #${containerId} .gjs-sm-title {
            color: #1e293b !important;
          }

          #${containerId} .gjs-field {
            background-color: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            color: #1e293b !important;
          }

          /* Color picker */
          #${containerId} .gjs-field-color-picker,
          #${containerId} .gjs-two-color {
            background-color: #ffffff !important;
            color: #1e293b !important;
          }

          /* Asset Manager Modal */
          #${containerId} .gjs-mdl-dialog {
            background-color: #ffffff !important;
            color: #1e293b !important;
          }

          #${containerId} .gjs-am-assets-cont,
          #${containerId} .gjs-am-file-uploader {
            background-color: #f8fafc !important;
          }

          #${containerId} .gjs-am-file-uploader label,
          #${containerId} .gjs-am-upload-text,
          #${containerId} .gjs-am-file-uploader .gjs-am-upload-text {
            color: #334155 !important;
            font-weight: 500 !important;
            font-size: 15px !important;
          }

          #${containerId} .gjs-am-modal input[type='text'] {
            background-color: #ffffff !important;
            color: #1e293b !important;
            border: 2px solid #64748b !important;
            border-radius: 6px !important;
            padding: 10px !important;
          }

          #${containerId} .gjs-am-modal input::placeholder {
            color: #64748b !important;
          }

          #${containerId} .gjs-am-modal .gjs-btn-prim,
          #${containerId} .gjs-am-modal button {
            background-color: #3498db !important;
            color: white !important;
            border: none !important;
            padding: 8px 16px !important;
            border-radius: 4px !important;
          }

          #${containerId} .gjs-am-add-asset button {
            width: 28% !important;
            background-color: #3498db !important;
            color: white;
            margin-left: 5px !important;
          }

          #${containerId} .gjs-am-modal .gjs-file-input-label {
            color: #1e293b !important;
          }
        `}</style>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    );
  }
);

Editor.displayName = 'Editor';

export default Editor;